"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
const react_1 = __importStar(require("react"));
const Typography_1 = __importDefault(require("@material-ui/core/Typography"));
const Menu_1 = __importDefault(require("@material-ui/core/Menu"));
const MenuItem_1 = __importDefault(require("@material-ui/core/MenuItem"));
const ArrowDropDown_1 = __importDefault(require("@material-ui/icons/ArrowDropDown"));
const styles_1 = require("@material-ui/core/styles");
const clsx_1 = __importDefault(require("clsx"));
const uuid_1 = require("uuid");
const Box_1 = __importDefault(require("../../layouts/Box"));
const Button_1 = __importDefault(require("../Button"));
const useUniqueId_1 = __importDefault(require("../../hooks/useUniqueId"));
const useStyles = styles_1.makeStyles((theme) => ({
    menuItem: {
        padding: '5px 20px',
    },
    subHeading: {
        padding: '5px 20px',
        fontSize: '14px',
        fontWeight: 700,
        color: theme.palette.grey[600],
    },
    disabledSubHeading: {
        opacity: '0.5',
        cursor: 'default',
    },
    childMenuItem: {
        paddingLeft: '24px',
        '&:first-of-type': {
            borderTop: `1px solid ${theme.palette.grey[200]}`,
        },
        '&:last-of-type': {
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
        },
    },
    darkTheme: {
        '& .MuiButton-root': {
            border: 'none',
            padding: '4px 5px',
            color: 'currentColor',
            '&:hover': {
                color: 'currentColor',
            },
        },
    },
}));
/**
 * A button dropdown is used to group a set of actions under one button.
 */
const ButtonDropdown = (_a) => {
    var { content, items = [], variant = 'normal', loading, disabled, disableArrowDropdown = false, menuItemClassName, darkTheme, onClick } = _a, props = __rest(_a, ["content", "items", "variant", "loading", "disabled", "disableArrowDropdown", "menuItemClassName", "darkTheme", "onClick"]);
    const classes = useStyles();
    const menuId = useUniqueId_1.default();
    const [anchorEl, setAnchorEl] = react_1.default.useState(null);
    const testId = props['data-testid'] || 'button-dropdown';
    const handleClick = react_1.useCallback((event) => {
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
        if (items.length > 0) {
            setAnchorEl(event.currentTarget);
        }
    }, [onClick, items, setAnchorEl]);
    const handleClose = react_1.useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);
    const handleMenuItemClick = react_1.useCallback((item) => {
        return (e) => {
            var _a;
            (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, e);
            if (!(e.isDefaultPrevented() || e.isPropagationStopped())) {
                handleClose();
            }
        };
    }, [handleClose]);
    react_1.useEffect(() => {
        if (items.length === 0 && anchorEl != null) {
            handleClose();
        }
    }, [items, anchorEl, handleClose]);
    const renderMenuItem = react_1.useCallback((item) => (react_1.default.createElement(MenuItem_1.default, { key: uuid_1.v4(), className: clsx_1.default(menuItemClassName, classes.menuItem), onClick: handleMenuItemClick(item), disabled: item.disabled, dense: true }, item.text)), [menuItemClassName, handleMenuItemClick, classes.menuItem]);
    const renderMenuItemWithHeading = react_1.useCallback((item) => {
        var _a;
        return (react_1.default.createElement(Box_1.default, { key: uuid_1.v4() },
            react_1.default.createElement(Typography_1.default, { className: clsx_1.default(classes.subHeading, { [classes.disabledSubHeading]: item.disabled }), variant: "subtitle1" }, item.text), (_a = item.items) === null || _a === void 0 ? void 0 :
            _a.map((itemChild) => (react_1.default.createElement(MenuItem_1.default, { key: uuid_1.v4(), className: clsx_1.default(menuItemClassName, classes.menuItem, classes.childMenuItem), onClick: itemChild.onClick, disabled: item.disabled || itemChild.disabled, dense: true }, itemChild.text)))));
    }, [classes, menuItemClassName]);
    return (react_1.default.createElement(Box_1.default, { className: clsx_1.default({ [classes.darkTheme]: darkTheme }), "data-testid": testId },
        react_1.default.createElement(Button_1.default, { variant: variant, onClick: handleClick, loading: loading, disabled: disabled, "data-testid": `${testId}-button`, "aria-haspopup": "true", "aria-controls": menuId },
            content,
            " ",
            !disableArrowDropdown && react_1.default.createElement(ArrowDropDown_1.default, { fontSize: "small" })),
        react_1.default.createElement(Menu_1.default, { id: menuId, anchorEl: anchorEl, keepMounted: true, getContentAnchorEl: null, anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
            }, elevation: 2, transitionDuration: 0, open: Boolean(anchorEl), onClose: handleClose, "data-testid": `${testId}-menu` }, items.map((item) => item.items ? renderMenuItemWithHeading(item) : renderMenuItem(item)))));
};
exports.default = ButtonDropdown;
//# sourceMappingURL=index.js.map